/**
 * AGNT text link with a trailing icon and a single left-to-right underline
 * animation on hover (the `.tx-lk` / `.rp-vw` treatment from the design mock).
 *
 * Recurs across the AGNT homepage ("View All Financials →", "View All News →",
 * etc.), so it lives here as one reusable piece. The underline is a single line
 * spanning the whole link (text + icon), not the per-segment underline the
 * global `a:hover` rule would otherwise draw on an inline-flex element.
 *
 * `tone` sets the colour via a CSS variable (so hover keeps the brand colour
 * instead of the global `a:hover` grey). Internal hrefs (`/…`, `#…`) stay in the
 * same tab; external open in a new tab.
 */

import { type ReactNode } from 'react';

import styles from './agnt-text-icon.module.scss';

interface AgntTextIconProps {
  href: string;
  label: string;
  /** Trailing icon. Defaults to a right arrow. */
  icon?: ReactNode;
  /** Brand colour for text + underline. Defaults to blue. */
  tone?: 'blue' | 'navy';
  /** Extra class for per-use overrides (e.g. font-size). */
  className?: string;
}

export default function AgntTextIcon({ href, label, icon = '→', tone = 'blue', className }: AgntTextIconProps) {
  const isInternal = href.startsWith('/') || href.startsWith('#');
  const classNames = [styles.link, tone === 'navy' ? styles.toneNavy : styles.toneBlue, className]
    .filter(Boolean)
    .join(' ');

  return (
    <a
      className={classNames}
      href={href}
      {...(isInternal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
    >
      <span>{label}</span>
      <span className={styles.icon} aria-hidden="true">{icon}</span>
    </a>
  );
}
